import React, { useCallback, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { useClickAway } from 'react-use';

interface TooltipProps {
  children: React.ReactElement;

  content?: string | React.ReactElement | null;

  trigger?: 'click' | 'hover';
}

export const Tooltip: React.FC<TooltipProps> = ({ content = null, children, trigger = 'hover' }) => {
  const ref = useRef(null);
  const [popperElement, setPopperElement] = useState(null);

  const [visible, setVisible] = useState(false);
  const [arrowElement, setArrowElement] = useState(null);

  const showTooltip = useCallback(() => setVisible(true), []);
  const hideTooltip = useCallback(() => setVisible(false), []);

  const { styles, attributes } = usePopper(ref.current, popperElement, {
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ],
    placement: 'top',
  });

  useClickAway(ref, hideTooltip);

  const onPointerEnter = trigger === 'hover' ? showTooltip : undefined;
  const onPointerLeave = trigger === 'hover' ? hideTooltip : undefined;

  if (!content) {
    return children;
  }

  return (
    <>
      <div ref={ref} onClick={showTooltip} onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave}>
        {children}
      </div>

      {visible && (
        <div ref={setPopperElement} className="tooltip" style={styles.popper} {...attributes.popper}>
          {content}
          <div ref={setArrowElement} className="tooltip__arrow" style={styles.arrow} />
        </div>
      )}
    </>
  );
};
